import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

export class BundleBuilderQuickViewComponent extends Component {
  requiredRefs = ['panel', 'overlay', 'closeButton', 'body', 'error', 'errorLink', 'injected'];

  /** @type {AbortController | null} */
  #openController = null;

  /** @type {HTMLElement | null} */
  #previousFocus = null;

  /** @type {number} */
  #lockedScrollY = 0;

  /** @type {number} */
  #openedAt = 0;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    this.close();
    super.disconnectedCallback();
  }

  /** @param {MouseEvent} event */
  openFromTrigger(event) {
    const actualTarget = /** @type {Element | null} */ (event.composedPath?.()[0] ?? null);
    if (!(actualTarget instanceof Element)) return;
    if (actualTarget.closest('bundle-builder-quantity-selector-component')) return;
    if (actualTarget.closest('[class*="yotpo"]')) return;

    const card = actualTarget.closest('bundle-builder-product-card-component');
    if (!card) return;
    const productUrl = card.getAttribute('data-quick-view-url');
    const template = /** @type {HTMLTemplateElement | null} */ (card.querySelector('[data-quick-view-content]'));

    if (!productUrl || !template) return;

    const trigger = /** @type {HTMLElement | null} */ (
      card.querySelector('.bundle-builder-product-card__qv-trigger')
    );
    this.open(productUrl, template, trigger);
  }

  /**
   * @param {string} productUrl
   * @param {HTMLTemplateElement} template
   * @param {HTMLElement | null} [trigger]
   */
  open(productUrl, template, trigger = null) {
    // Only restore focus to the trigger on close if keyboard was already on it when the panel opened.
    // Mouse clicks don't move keyboard focus, so activeElement won't match the trigger.
    this.#previousFocus = (trigger && document.activeElement === trigger)
      ? trigger
      : null;

    this.#injectContent(template, productUrl);
    this.#openPanel();
  }

  close() {
    if (this.getAttribute('aria-hidden') === 'true') return;

    this.setAttribute('aria-hidden', 'true');
    const panel = /** @type {HTMLElement} */ (this.refs.panel);
    panel.setAttribute('aria-hidden', 'true');

    removeTrapFocus();
    this.#unlockPageScroll();

    this.#openController?.abort();
    this.#openController = null;

    const previous = this.#previousFocus;
    this.#previousFocus = null;
    if (previous) {
      requestAnimationFrame(() => previous.focus());
    }
  }

  #lockPageScroll() {
    this.#lockedScrollY = window.scrollY;
    document.documentElement.setAttribute('scroll-lock', '');
    if (this.#lockedScrollY > 0) {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#lockedScrollY}px`;
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  #unlockPageScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.overflow = '';
    window.scrollTo({ top: this.#lockedScrollY, behavior: 'instant' });
  }

  #openPanel() {
    this.setAttribute('aria-hidden', 'false');
    const panel = /** @type {HTMLElement} */ (this.refs.panel);
    panel.removeAttribute('aria-hidden');
    this.#lockPageScroll();

    this.#openedAt = Date.now();
    this.#openController?.abort();
    this.#openController = new AbortController();
    const signal = this.#openController.signal;

    document.addEventListener('keydown', this.#handleKeyDown, { signal });

    const overlay = /** @type {HTMLElement} */ (this.refs.overlay);
    overlay.addEventListener('click', () => {
      if (Date.now() - this.#openedAt < 300) return;
      this.close();
    }, { signal });

    requestAnimationFrame(() => {
      trapFocus(panel);
    });
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  /**
   * @param {HTMLTemplateElement} template
   * @param {string} productUrl
   */
  #injectContent(template, productUrl) {
    const error = /** @type {HTMLElement} */ (this.refs.error);
    const injected = /** @type {HTMLElement} */ (this.refs.injected);

    error.hidden = true;
    injected.hidden = false;
    injected.innerHTML = '';
    injected.appendChild(template.content.cloneNode(true));

    this.#rehydrateScripts(injected);
    this.#reinitYotpo();

    const pdpLinks = injected.querySelectorAll('.bb-quick-view__pdp-anchor');
    for (const link of pdpLinks) {
      link.setAttribute('href', productUrl);
    }

    /** @type {HTMLElement} */ (this.refs.body).scrollTop = 0;

    requestAnimationFrame(() => {
      trapFocus(/** @type {HTMLElement} */ (this.refs.panel));
    });
  }

  /**
   * Ask Yotpo to scan for newly injected widget instances.
   * The widget markup is a plain div with data-attributes — no inline scripts —
   * so script rehydration alone is not enough.
   */
  #reinitYotpo() {
    try {
      // eslint-disable-next-line no-undef
      if (typeof yotpoWidgetsContainer !== 'undefined' && typeof yotpoWidgetsContainer.initWidgets === 'function') {
        yotpoWidgetsContainer.initWidgets();
      } else if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
        window.yotpo.refreshWidgets();
      }
    } catch {
      // Yotpo init failure must not prevent the panel from opening
    }
  }

  /**
   * Re-execute scripts injected via cloneNode so third-party app widgets initialise.
   * @param {HTMLElement} container
   */
  #rehydrateScripts(container) {
    for (const oldScript of container.querySelectorAll('script')) {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    }
  }
}

if (!customElements.get('bundle-builder-quick-view-component')) {
  customElements.define('bundle-builder-quick-view-component', BundleBuilderQuickViewComponent);
}
